import { debounce } from 'lodash';
import { useCallback, useEffect, useState } from 'react';
import { endOfMonth, setMonth, setYear, startOfMonth } from 'date-fns';

import Badge from '@mui/material/Badge';
import Stack from '@mui/material/Stack';
import Divider from '@mui/material/Divider';
import TextField from '@mui/material/TextField';
import Typography from '@mui/material/Typography';
import InputAdornment from '@mui/material/InputAdornment';
import {
  Button,
  FormControl,
  FormControlLabel,
  IconButton,
  InputLabel,
  MenuItem,
  OutlinedInput,
  Select,
  SelectChangeEvent,
  Switch,
  Tooltip,
} from '@mui/material';

import { getMonthsOfCurrentYear } from 'src/utils/functions';

import Label from 'src/components/label';
import Iconify from 'src/components/iconify';
import CustomPopover, { usePopover } from 'src/components/custom-popover';
import CustomDatePicker from 'src/components/custom-datepicker/custom-datepicker';

import {
  ChequeCustomerType,
  ChequeStatus,
  FactoringStatus,
  IChequeReceived,
  IChequeTableFilters,
  IChequeTableFilterValue,
} from 'src/types/cheque';

type Props = {
  filters: IChequeTableFilters;
  onFilters: (name: string, value: IChequeTableFilterValue) => void;
  handleResetFilters: VoidFunction;
  canReset: boolean;
  activeFilterCount: number;
  received: IChequeReceived[];
  tableStatus: string;
};

export default function ChequeTableToolbar({
  filters,
  onFilters,
  handleResetFilters,
  canReset,
  activeFilterCount,
  received,
  tableStatus,
}: Props) {
  // Seeded from the filters so a list restored from the URL shows its search.
  const [input, setInput] = useState<string>(filters.accountNumber || filters.generalSearch || '');

  const [accountNumberMode, setAccountNumberMode] = useState(!!filters.accountNumber);

  const filtersPopover = usePopover();

  const months = getMonthsOfCurrentYear();
  const [month, setMonthState] = useState<string | null>(
    filters.startDate ? months[filters.startDate.getMonth()] : null
  );

  const [year, setYearState] = useState<number | null>(
    filters.startDate ? filters.startDate.getFullYear() : null
  );

  const handleFilterMonth = useCallback(
    (event: SelectChangeEvent<string>) => {
      const selectedMonth = event.target.value;
      setMonthState(selectedMonth);

      if (year) {
        const monthIndex = months.indexOf(selectedMonth);
        const selectedDate = setMonth(setYear(new Date(), year), monthIndex);

        onFilters('startDate', startOfMonth(selectedDate));
        onFilters('endDate', endOfMonth(selectedDate));
      }
    },
    [months, year, onFilters]
  );

  const handleFilterYear = (event: SelectChangeEvent<string>) => {
    const selectedYear = Number(event.target.value);
    setYearState(selectedYear);

    if (month) {
      const monthIndex = months.indexOf(month);
      const selectedDate = setMonth(setYear(new Date(), selectedYear), monthIndex);

      onFilters('startDate', startOfMonth(selectedDate));
      onFilters('endDate', endOfMonth(selectedDate));
    }
  };

  // eslint-disable-next-line react-hooks/exhaustive-deps
  const debouncedSearch = useCallback(
    debounce((value: string, isAccountNumber: boolean) => {
      onFilters(isAccountNumber ? 'accountNumber' : 'generalSearch', value);
    }, 500),
    []
  );

  const filterByReceived = useCallback((event: SelectChangeEvent<string>) => {
    onFilters('receivedVia', event.target.value);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const filterSaleType = useCallback((event: SelectChangeEvent<string>) => {
    onFilters('type', event.target.value);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const filterIsFactored = useCallback(
    (event: SelectChangeEvent<string>) => {
      const { value } = event.target;
      onFilters('isFactored', value === '' ? null : value === 'true');
      if (value !== 'true') {
        onFilters('factoringStatus', '');
      }
    },
    [onFilters]
  );

  const filterFactoringStatus = useCallback((event: SelectChangeEvent<string>) => {
    onFilters('factoringStatus', event.target.value);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // eslint-disable-next-line arrow-body-style
  useEffect(() => {
    return () => {
      debouncedSearch.cancel();
    };
  }, [debouncedSearch]);

  const handleSearchChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const { value } = event.target;

    setInput(value);

    debouncedSearch(value, accountNumberMode);
  };

  const handleToggleAccountNumberMode = (event: React.ChangeEvent<HTMLInputElement>) => {
    const { checked } = event.target;

    setAccountNumberMode(checked);
    setInput('');

    debouncedSearch.cancel();
    onFilters('generalSearch', '');
    onFilters('accountNumber', '');
  };

  const handleFilterReceivedDate = useCallback(
    (date: any) => {
      onFilters('receivedDate', date);
    },
    [onFilters]
  );

  const handleFilterPaidDate = useCallback(
    (date: any) => {
      onFilters('paidDate', date);
    },
    [onFilters]
  );

  const handleFilterBankedDate = useCallback(
    (date: any) => {
      onFilters('bankedDate', date);
    },
    [onFilters]
  );

  const handleFilterChequeDateUpTo = useCallback(
    (date: any) => {
      onFilters('chequeDateUpTo', date);
    },
    [onFilters]
  );

  const clearSearchText = () => {
    setInput('');

    debouncedSearch('', accountNumberMode);
  };

  const clearAll = useCallback(() => {
    setInput('');
    setAccountNumberMode(false);
    handleResetFilters();

    setMonthState(null);
    setYearState(null);
  }, [handleResetFilters]);

  return (
    <>
      <Stack
        spacing={2}
        alignItems="center"
        direction="row"
        sx={{
          p: 2.5,
        }}
      >
        <TextField
          fullWidth
          value={input}
          onChange={handleSearchChange}
          placeholder={accountNumberMode ? 'Search by account number...' : 'Search by anything...'}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <Iconify icon="eva:search-fill" sx={{ color: 'text.disabled' }} />
              </InputAdornment>
            ),
            endAdornment:
              input.length > 0 ? (
                <InputAdornment position="end">
                  <IconButton onClick={clearSearchText}>
                    <Iconify icon="eva:close-fill" color="text.disabled" />
                  </IconButton>
                </InputAdornment>
              ) : null,
          }}
          inputProps={{
            autoComplete: 'off',
          }}
        />

        <FormControlLabel
          sx={{ flexShrink: 0, mx: 0 }}
          label="Account No."
          control={
            <Switch
              size="small"
              checked={accountNumberMode}
              onChange={handleToggleAccountNumberMode}
            />
          }
        />

        {tableStatus !== ChequeStatus.PAID && tableStatus !== ChequeStatus.BANKING && (
          <CustomDatePicker
            disableFuture
            label="Received Date"
            value={filters.receivedDate}
            onChange={handleFilterReceivedDate}
            slotProps={{
              textField: {
                sx: { flexShrink: 0, width: { xs: 1, md: 180 } },
              },
            }}
          />
        )}

        {tableStatus === ChequeStatus.PAID && (
          <CustomDatePicker
            disableFuture
            label="Paid Date"
            value={filters.paidDate}
            onChange={handleFilterPaidDate}
            slotProps={{
              textField: {
                sx: { flexShrink: 0, width: { xs: 1, md: 180 } },
              },
            }}
          />
        )}

        {tableStatus === ChequeStatus.BANKING && (
          <CustomDatePicker
            disableFuture
            label="Banking Date"
            value={filters.bankedDate}
            onChange={handleFilterBankedDate}
            slotProps={{
              textField: {
                sx: { flexShrink: 0, width: { xs: 1, md: 180 } },
              },
            }}
          />
        )}

        {tableStatus === ChequeStatus.IN_HAND && (
          <Tooltip title="Only In Hand cheques dated on or before this day">
            <div>
              <CustomDatePicker
                disablePast
                label="Passing By"
                value={filters.chequeDateUpTo}
                onChange={handleFilterChequeDateUpTo}
                slotProps={{
                  textField: {
                    sx: { flexShrink: 0, width: { xs: 1, md: 180 } },
                  },
                }}
              />
            </div>
          </Tooltip>
        )}

        <Tooltip title="Filters">
          <Badge color="error" badgeContent={activeFilterCount} invisible={activeFilterCount === 0}>
            <IconButton
              onClick={filtersPopover.onOpen}
              color={filtersPopover.open ? 'inherit' : 'default'}
            >
              <Iconify icon="ic:round-filter-list" />
            </IconButton>
          </Badge>
        </Tooltip>

        {canReset && (
          <Button
            color="error"
            onClick={clearAll}
            variant="text"
            sx={{ textDecoration: 'underline', flexShrink: 0 }}
          >
            Clear
          </Button>
        )}
      </Stack>

      <CustomPopover
        open={filtersPopover.open}
        onClose={filtersPopover.onClose}
        arrow="top-right"
        sx={{ width: 320 }}
      >
        <Stack spacing={2.5} sx={{ p: 2.5 }}>
          <Typography variant="subtitle2">Filters</Typography>

          <Stack direction="row" spacing={2}>
            <FormControl fullWidth>
              <InputLabel>Year</InputLabel>

              <Select
                value={year ? year.toString() : ''}
                onChange={handleFilterYear}
                input={<OutlinedInput label="Year" />}
                renderValue={(selected) => (
                  <Label variant="soft" color="primary">
                    {selected}
                  </Label>
                )}
                sx={{ textTransform: 'capitalize' }}
              >
                {[
                  new Date().getFullYear() - 1,
                  new Date().getFullYear(),
                  new Date().getFullYear() + 1,
                ].map((option) => (
                  <MenuItem key={option} value={option}>
                    {option}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>

            <FormControl fullWidth>
              <InputLabel>Month</InputLabel>

              <Select
                value={month ?? ''}
                onChange={handleFilterMonth}
                input={<OutlinedInput label="Month" />}
                renderValue={(selected) => (
                  <Label variant="soft" color="primary">
                    {selected}
                  </Label>
                )}
                sx={{ textTransform: 'capitalize' }}
              >
                {months.map((option) => (
                  <MenuItem key={option} value={option}>
                    {option}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Stack>

          <Divider sx={{ borderStyle: 'dashed' }} />

          <FormControl fullWidth>
            <InputLabel>Received Via</InputLabel>

            <Select
              value={filters.receivedVia ?? ''}
              onChange={filterByReceived}
              input={<OutlinedInput label="Received Via" />}
              renderValue={(selected) => (
                <Label variant="soft" color="primary">
                  {selected}
                </Label>
              )}
              sx={{ textTransform: 'capitalize' }}
            >
              {received.map((option) => (
                <MenuItem key={option.receivedVia} value={option.receivedVia}>
                  {option.receivedVia}
                </MenuItem>
              ))}
            </Select>
          </FormControl>

          {(tableStatus === ChequeStatus.PAID || tableStatus === ChequeStatus.BANKING) && (
            <CustomDatePicker
              disableFuture
              label="Received Date"
              value={filters.receivedDate}
              onChange={handleFilterReceivedDate}
              slotProps={{ textField: { fullWidth: true } }}
            />
          )}

          {tableStatus === 'all' && (
            <CustomDatePicker
              disableFuture
              label="Paid Date"
              value={filters.paidDate}
              onChange={handleFilterPaidDate}
              slotProps={{ textField: { fullWidth: true } }}
            />
          )}

          <FormControl fullWidth>
            <InputLabel>Sale Type</InputLabel>

            <Select
              value={filters.type ?? ''}
              onChange={filterSaleType}
              input={<OutlinedInput label="Sale Type" />}
              renderValue={(selected) => (
                <Label variant="soft" color="primary">
                  {selected}
                </Label>
              )}
              sx={{ textTransform: 'capitalize' }}
            >
              {Object.values(ChequeCustomerType).map((option) => (
                <MenuItem
                  key={option}
                  value={option}
                  sx={{
                    textTransform: 'capitalize',
                  }}
                >
                  {option}
                </MenuItem>
              ))}
            </Select>
          </FormControl>

          <FormControl fullWidth>
            <InputLabel>Factoring</InputLabel>

            <Select
              value={filters.isFactored === null ? '' : String(filters.isFactored)}
              onChange={filterIsFactored}
              input={<OutlinedInput label="Factoring" />}
              renderValue={(selected) => (
                <Label variant="soft" color="primary">
                  {selected === 'true' ? 'Factored' : 'Not Factored'}
                </Label>
              )}
            >
              <MenuItem value="true">Factored</MenuItem>
              <MenuItem value="false">Not Factored</MenuItem>
            </Select>
          </FormControl>

          {filters.isFactored === true && (
            <FormControl fullWidth>
              <InputLabel>Factoring Status</InputLabel>

              <Select
                value={filters.factoringStatus ?? ''}
                onChange={filterFactoringStatus}
                input={<OutlinedInput label="Factoring Status" />}
                renderValue={(selected) => (
                  <Label variant="soft" color="primary">
                    {selected}
                  </Label>
                )}
              >
                {Object.values(FactoringStatus).map((option) => (
                  <MenuItem key={option} value={option}>
                    {option}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          )}
        </Stack>
      </CustomPopover>
    </>
  );
}
